/**
 * Our Square Resident Portal
 * 
 * Features:
 * - Dashboard with progress overview
 * - View assigned classes and tasks
 * - Submit task completions with file uploads
 * - View approved milestones
 * - Track progress toward phases
 * - View house rules and meeting schedule
 * - Message with peer specialist
 * - Download certificates
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import DictationInput from '../../components/DictationInput';
import VoiceRecorder from '../../components/VoiceRecorder';
import { storeReflection } from '../../lib/aiReflections';
import { 
  Home, BookOpen, Target, CheckCircle2, Clock, Upload, Award, 
  MessageSquare, FileText, Calendar, ChevronRight, User, Star,
  Heart, Brain, Briefcase, Users, Sparkles, Lightbulb, TreePine, DollarSign,
  ArrowLeft, Download, Send
} from 'lucide-react';

const CATEGORY_ICONS = {
  'Emotional Wellness': Heart,
  'Physical Wellness': Target,
  'Occupational Wellness': Briefcase,
  'Social Wellness': Users,
  'Spiritual Wellness': Sparkles,
  'Intellectual Wellness': Lightbulb,
  'Environmental Wellness': TreePine,
  'Financial Wellness': DollarSign,
};

const STATUS_CONFIG = {
  not_started: { color: 'bg-slate-100 text-slate-600', label: 'Not Started', icon: Clock },
  pending: { color: 'bg-amber-50 text-amber-700', label: 'Pending Review', icon: Clock },
  approved: { color: 'bg-emerald-50 text-emerald-700', label: 'Completed', icon: CheckCircle2 },
  revision_needed: { color: 'bg-red-50 text-red-700', label: 'Needs Revision', icon: FileText },
};

export default function ResidentPortal() {
  const { user, axiosAuth } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedClass, setSelectedClass] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [submissionText, setSubmissionText] = useState('');
  const [originalSubmission, setOriginalSubmission] = useState('');
  const [submissionFile, setSubmissionFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [housingInfo, setHousingInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  const api = axiosAuth();

  const loadProgress = useCallback(async () => {
    try {
      const res = await api.get('/curriculum/my-progress');
      setProgress(res.data);
    } catch (e) {
      toast.error('Failed to load curriculum progress');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadHousingInfo = useCallback(async () => {
    try {
      const res = await api.get('/housing/my-housing');
      setHousingInfo(res.data);
    } catch (e) {
      console.warn('Housing info not available:', e.message);
    }
  }, [api]);

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get('/messages/conversation', {
        params: { other_user_id: progress?.peer_specialist?.id }
      });
      setMessages(res.data || []);
    } catch (e) {
      console.warn('Messages not available:', e.message);
    }
  }, [api, progress?.peer_specialist?.id]);

  useEffect(() => {
    loadProgress();
    loadHousingInfo();
  }, [loadProgress, loadHousingInfo]);

  useEffect(() => {
    if (progress?.peer_specialist?.id) {
      loadMessages();
    }
  }, [progress?.peer_specialist?.id, loadMessages]);

  const handleSubmitTask = async () => {
    if (!selectedTask || !progress?.progress?.enrollment?.id) return;
    setSubmitting(true);
    try {
      // Create milestone submission
      const res = await api.post('/curriculum/milestones', null, {
        params: {
          enrollment_id: progress.progress.enrollment.id,
          task_id: selectedTask.id,
          submission_text: submissionText,
          original_text: originalSubmission || submissionText
        }
      });
      
      // Upload file if provided
      if (submissionFile && res.data.id) {
        const formData = new FormData();
        formData.append('file', submissionFile);
        await api.post(`/curriculum/milestones/${res.data.id}/upload`, formData);
      }
      
      toast.success('Task submitted for review!');
      if (submissionText?.trim()) {
        await storeReflection(api, {
          original_text: originalSubmission || submissionText,
          refined_text: submissionText,
          context: 'curriculum_task',
          task_name: selectedTask.name,
          task_description: selectedTask.description,
          category: selectedClass?.category || 'general',
          source: 'resident_portal',
          related_id: selectedTask.id
        });
      }
      setSubmitOpen(false);
      setSelectedTask(null);
      setSubmissionText('');
      setSubmissionFile(null);
      loadProgress();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !progress?.peer_specialist?.id) return;
    try {
      await api.post('/messages', {
        recipient_id: progress.peer_specialist.id,
        content: newMessage
      });
      setNewMessage('');
      loadMessages();
      toast.success('Message sent!');
    } catch {
      toast.error('Failed to send message');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (!progress?.enrolled) {
    return (
      <div className="space-y-6" data-testid="resident-not-enrolled">
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-8 text-center">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Not Yet Enrolled</h2>
            <p className="text-muted-foreground">You haven't been enrolled in a curriculum program yet. Please speak with your coordinator.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { progress: prog, peer_specialist, certificates } = progress;
  const { enrollment, current_phase, phases, class_progress, summary } = prog;

  return (
    <div className="space-y-6" data-testid="resident-portal">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
          My Progress
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Our Square Women's Housing Program</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-br from-cyan-50 to-teal-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-cyan-700">{summary.completed}</p>
            <p className="text-xs text-cyan-600">Tasks Complete</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{summary.pending}</p>
            <p className="text-xs text-muted-foreground">Pending Review</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-700">{summary.not_started}</p>
            <p className="text-xs text-muted-foreground">Remaining</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">{certificates?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Certificates</p>
          </CardContent>
        </Card>
      </div>

      {/* Current Phase Card */}
      <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-cyan-500">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Current Phase</p>
              <h3 className="text-lg font-bold text-slate-900">{current_phase.name}</h3>
              <p className="text-sm text-muted-foreground">{current_phase.description}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-cyan-600">{summary.overall_progress}%</p>
              <p className="text-xs text-muted-foreground">Overall Progress</p>
            </div>
          </div>
          <Progress value={summary.overall_progress} className="h-2" />
          <div className="flex gap-1 mt-3">
            {phases.map((p, i) => (
              <div 
                key={p.id} 
                className={`flex-1 h-1.5 rounded-full ${i < enrollment.current_phase_order ? 'bg-cyan-500' : i === enrollment.current_phase_order - 1 ? 'bg-cyan-300' : 'bg-slate-200'}`}
              />
            ))}
          </div>
          <div className="flex justify-between mt-1">
            {phases.map(p => (
              <p key={p.id} className={`text-[10px] ${p.id === current_phase.id ? 'text-cyan-600 font-medium' : 'text-muted-foreground'}`}>{p.name}</p>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-5 h-auto p-1 bg-slate-100 rounded-xl">
          <TabsTrigger value="dashboard" className="rounded-lg text-xs py-2 data-[state=active]:bg-white">
            <Home size={14} className="mr-1" /> Home
          </TabsTrigger>
          <TabsTrigger value="classes" className="rounded-lg text-xs py-2 data-[state=active]:bg-white">
            <BookOpen size={14} className="mr-1" /> Classes
          </TabsTrigger>
          <TabsTrigger value="housing" className="rounded-lg text-xs py-2 data-[state=active]:bg-white">
            <Target size={14} className="mr-1" /> Housing
          </TabsTrigger>
          <TabsTrigger value="messages" className="rounded-lg text-xs py-2 data-[state=active]:bg-white">
            <MessageSquare size={14} className="mr-1" /> Messages
          </TabsTrigger>
          <TabsTrigger value="certificates" className="rounded-lg text-xs py-2 data-[state=active]:bg-white">
            <Award size={14} className="mr-1" /> Awards
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* Peer Specialist Card */}
          {peer_specialist && (
            <Card className="border-0 shadow-sm rounded-xl">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
                  <User size={24} className="text-cyan-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">Your Peer Specialist</p>
                  <p className="font-semibold text-slate-900">{peer_specialist.full_name}</p>
                  {peer_specialist.email && <p className="text-xs text-cyan-600">{peer_specialist.email}</p>}
                </div>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setActiveTab('messages')}>
                  <MessageSquare size={14} className="mr-1" /> Message
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Classes Progress Overview */}
          <Card className="border-0 shadow-sm rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BookOpen size={16} className="text-cyan-600" /> 8 Dimensions of Wellness
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {class_progress.map(cls => {
                const Icon = CATEGORY_ICONS[cls.category] || BookOpen;
                return (
                  <div 
                    key={cls.id} 
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => { setSelectedClass(cls); setActiveTab('classes'); }}
                    data-testid={`class-${cls.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                      <Icon size={18} className="text-cyan-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">{cls.category}</p>
                      <p className="text-xs text-muted-foreground">{cls.completed}/{cls.total} tasks</p>
                    </div>
                    <div className="w-20">
                      <Progress value={cls.progress_percent} className="h-1.5" />
                    </div>
                    <ChevronRight size={14} className="text-muted-foreground" />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Classes Tab */}
        <TabsContent value="classes" className="space-y-4">
          {selectedClass ? (
            <div className="space-y-4">
              <Button variant="ghost" size="sm" onClick={() => setSelectedClass(null)} className="rounded-full">
                <ArrowLeft size={14} className="mr-1" /> Back to Classes
              </Button>
              
              <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-cyan-500">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    {(() => { const Icon = CATEGORY_ICONS[selectedClass.category] || BookOpen; return <Icon size={24} className="text-cyan-600" />; })()}
                    <div>
                      <CardTitle>{selectedClass.category}</CardTitle>
                      <p className="text-sm text-muted-foreground">{selectedClass.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {selectedClass.tasks.map(task => {
                    const config = STATUS_CONFIG[task.status] || STATUS_CONFIG.not_started;
                    const StatusIcon = config.icon;
                    return (
                      <div key={task.id} className="p-4 rounded-xl border border-slate-100 bg-white">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={`${config.color} border-0 text-xs`}>
                                <StatusIcon size={10} className="mr-1" /> {config.label}
                              </Badge>
                              {task.requires_upload && <Badge variant="outline" className="text-xs">File Required</Badge>}
                            </div>
                            <p className="font-medium text-sm text-slate-900">{task.name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>
                            {task.milestone?.reviewer_notes && task.status === 'approved' && (
                              <p className="text-xs text-emerald-600 mt-2 bg-emerald-50 rounded-lg p-2">
                                <strong>Feedback:</strong> {task.milestone.reviewer_notes}
                              </p>
                            )}
                            {task.milestone?.reviewer_notes && task.status === 'revision_needed' && (
                              <p className="text-xs text-red-600 mt-2 bg-red-50 rounded-lg p-2">
                                <strong>Revision Needed:</strong> {task.milestone.reviewer_notes}
                              </p>
                            )}
                          </div>
                          {task.status !== 'approved' && (
                            <Dialog open={submitOpen && selectedTask?.id === task.id} onOpenChange={(open) => { setSubmitOpen(open); if (!open) setSelectedTask(null); }}>
                              <DialogTrigger asChild>
                                <Button 
                                  size="sm" 
                                  className="rounded-full bg-cyan-600 hover:bg-cyan-700"
                                  onClick={() => { setSelectedTask(task); setSubmitOpen(true); }}
                                  data-testid={`submit-task-${task.id}`}
                                >
                                  {task.status === 'pending' ? 'Update' : task.status === 'revision_needed' ? 'Resubmit' : 'Submit'}
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="rounded-2xl">
                                <DialogHeader>
                                  <DialogTitle>Submit: {task.name}</DialogTitle>
                                </DialogHeader>
                                  <div className="space-y-4 pt-2">
                                  <p className="text-sm text-muted-foreground">{task.description}</p>
                                  <div className="space-y-1.5">
                                    <Label>Your Submission</Label>
                                    <DictationInput
                                      value={submissionText}
                                      onChange={(cleaned, original) => {
                                        setSubmissionText(cleaned);
                                        setOriginalSubmission(original);
                                      }}
                                      placeholder="Describe what you completed, what you learned, or any notes... You can type or tap the mic to speak."
                                      rows={4}
                                      promptContext={{
                                        taskName: task.name,
                                        taskDescription: task.description,
                                        category: selectedClass?.category || '',
                                        context: 'general'
                                      }}
                                      data-testid="submission-dictation"
                                    />
                                  </div>
                                  {task.requires_upload && (
                                    <div className="space-y-1.5">
                                      <Label>Upload File (Required)</Label>
                                      <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center">
                                        <Input 
                                          type="file" 
                                          onChange={e => setSubmissionFile(e.target.files[0])}
                                          className="hidden"
                                          id="file-upload"
                                        />
                                        <label htmlFor="file-upload" className="cursor-pointer">
                                          <Upload size={24} className="mx-auto text-slate-400 mb-2" />
                                          <p className="text-sm text-muted-foreground">
                                            {submissionFile ? submissionFile.name : 'Click to upload your completed work'}
                                          </p>
                                        </label>
                                      </div>
                                    </div>
                                  )}
                                  <Button 
                                    onClick={handleSubmitTask} 
                                    disabled={submitting || (task.requires_upload && !submissionFile)}
                                    className="w-full bg-cyan-600 hover:bg-cyan-700 rounded-full"
                                  >
                                    {submitting ? 'Submitting...' : 'Submit for Review'}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Select a wellness dimension to view tasks</p>
              {class_progress.map(cls => {
                const Icon = CATEGORY_ICONS[cls.category] || BookOpen;
                return (
                  <Card 
                    key={cls.id} 
                    className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedClass(cls)}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-cyan-50 flex items-center justify-center">
                        <Icon size={24} className="text-cyan-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">{cls.category}</p>
                        <p className="text-xs text-muted-foreground">{cls.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-cyan-600">{cls.progress_percent}%</p>
                        <p className="text-xs text-muted-foreground">{cls.completed}/{cls.total}</p>
                      </div>
                      <ChevronRight size={16} className="text-muted-foreground" />
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Housing Tab */}
        <TabsContent value="housing" className="space-y-4">
          {housingInfo ? (
            <>
              {/* My Housing Info */}
              <Card className="border-0 shadow-sm rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Home size={16} className="text-cyan-600" /> My Housing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-muted-foreground">House</p>
                      <p className="font-medium text-sm">{housingInfo.house_name || 'N/A'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-muted-foreground">Room / Bed</p>
                      <p className="font-medium text-sm">{housingInfo.room_label || 'N/A'} / {housingInfo.bed_label || 'N/A'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-muted-foreground">Move-in Date</p>
                      <p className="font-medium text-sm">{housingInfo.move_in_date || 'N/A'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-muted-foreground">Program Phase</p>
                      <Badge className="bg-cyan-50 text-cyan-700 border-0 capitalize">{housingInfo.program_phase}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Curfew Compliance */}
              <Card className="border-0 shadow-sm rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock size={16} className="text-cyan-600" /> Curfew History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {housingInfo.curfew_logs?.length > 0 ? (
                    <div className="space-y-2">
                      {housingInfo.curfew_logs.slice(0, 10).map((log, i) => (
                        <div key={`item-${i}`} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                          <div>
                            <p className="text-sm font-medium">{log.date}</p>
                            <p className="text-xs text-muted-foreground">Curfew: {log.curfew_time}</p>
                          </div>
                          <Badge className={log.status === 'on_time' ? 'bg-emerald-50 text-emerald-700 border-0' : log.status === 'excused' ? 'bg-blue-50 text-blue-700 border-0' : 'bg-red-50 text-red-700 border-0'}>
                            {log.status?.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No curfew logs yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Drug Tests */}
              <Card className="border-0 shadow-sm rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target size={16} className="text-cyan-600" /> Drug Test History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {housingInfo.drug_tests?.length > 0 ? (
                    <div className="space-y-2">
                      {housingInfo.drug_tests.slice(0, 10).map((test, i) => (
                        <div key={`item-${i}`} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                          <div>
                            <p className="text-sm font-medium">{new Date(test.created_at).toLocaleDateString()}</p>
                            <p className="text-xs text-muted-foreground capitalize">{test.test_type} test</p>
                          </div>
                          <Badge className={test.result === 'negative' ? 'bg-emerald-50 text-emerald-700 border-0' : test.result === 'pending' ? 'bg-amber-50 text-amber-700 border-0' : 'bg-red-50 text-red-700 border-0'}>
                            {test.result === 'negative' ? 'Pass' : test.result === 'pending' ? 'Pending' : 'Fail'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No drug tests yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Goals */}
              <Card className="border-0 shadow-sm rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Star size={16} className="text-cyan-600" /> My Goals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {housingInfo.goals?.length > 0 ? (
                    <div className="space-y-2">
                      {housingInfo.goals.map((goal, i) => (
                        <div key={`item-${i}`} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                          {goal.status === 'completed' ? (
                            <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                          ) : (
                            <Clock size={16} className="text-amber-500 shrink-0" />
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium">{goal.title}</p>
                            {goal.category && <p className="text-xs text-muted-foreground capitalize">{goal.category}</p>}
                          </div>
                          <Badge variant="outline" className="text-xs">{goal.status}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No goals set yet</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-0 shadow-sm rounded-xl">
              <CardContent className="p-8 text-center">
                <Home size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">Housing information not available</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          {peer_specialist ? (
            <Card className="border-0 shadow-sm rounded-xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare size={16} className="text-cyan-600" /> Chat with {peer_specialist.full_name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 overflow-y-auto mb-4 p-3 bg-slate-50 rounded-xl space-y-3">
                  {messages.length > 0 ? (
                    messages.map((msg, i) => (
                      <div key={`item-${i}`} className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] p-3 rounded-xl ${msg.sender_id === user.id ? 'bg-cyan-600 text-white' : 'bg-white border'}`}>
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-[10px] mt-1 ${msg.sender_id === user.id ? 'text-cyan-100' : 'text-muted-foreground'}`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-sm text-muted-foreground py-8">No messages yet. Send a message to your peer specialist!</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Input 
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      onKeyPress={e => e.key === 'Enter' && sendMessage()}
                      className="rounded-full pr-10"
                    />
                    <div className="absolute right-1 top-1">
                      <VoiceRecorder onTranscription={(text) => setNewMessage(prev => prev ? `${prev} ${text}` : text)} size="icon" />
                    </div>
                  </div>
                  <Button onClick={sendMessage} className="rounded-full bg-cyan-600 hover:bg-cyan-700">
                    <Send size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm rounded-xl">
              <CardContent className="p-8 text-center">
                <MessageSquare size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No peer specialist assigned yet</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates" className="space-y-4">
          {certificates?.length > 0 ? (
            certificates.map(cert => (
              <Card key={cert.id} className="border-0 shadow-sm rounded-xl bg-gradient-to-br from-amber-50 to-orange-50">
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Award size={28} className="text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-slate-900">{cert.title}</p>
                      <p className="text-sm text-muted-foreground">{cert.description}</p>
                      <p className="text-xs text-amber-700 mt-1">Issued: {new Date(cert.issued_at).toLocaleDateString()}</p>
                    </div>
                    <Button variant="outline" size="sm" className="rounded-full gap-1">
                      <Download size={14} /> Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border-0 shadow-sm rounded-xl">
              <CardContent className="p-8 text-center">
                <Award size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="font-medium text-slate-900">No Certificates Yet</p>
                <p className="text-sm text-muted-foreground mt-1">Complete program milestones to earn certificates!</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
